import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import Card from './shared/Card';
import Alert from './shared/Alert';
import InfoRow from './shared/InfoRow';
import Spinner from './shared/Spinner';

const VECTOR_STORE_OPTIONS = [
    { value: 'qdrant', label: 'Qdrant' },
    { value: 's3', label: 'S3 Vectors' },
    { value: 'gemini_file_search', label: 'Gemini File Search Store' },
];

const DEVICE_OPTIONS = [
    { value: 'cpu', label: 'CPU' },
    { value: 'cuda', label: 'CUDA (GPU)' },
    { value: 'mps', label: 'MPS (Apple Silicon)' },
];

const ALL_MODELS = [
    { value: 'AITeamVN/Vietnamese_Embedding_v2', label: 'Vietnamese Embedding v2 (1024d, Vietnamese)', provider: 'sentence_transformer' },
    { value: 'BAAI/bge-m3', label: 'BAAI/bge-m3 (1024d, multilingual)', provider: 'sentence_transformer' },
    { value: 'BAAI/bge-large-en-v1.5', label: 'BAAI/bge-large-en-v1.5 (1024d, English)', provider: 'sentence_transformer' },
    { value: 'sentence-transformers/all-MiniLM-L6-v2', label: 'all-MiniLM-L6-v2 (384d, fast)', provider: 'sentence_transformer' },
    { value: 'gemini-embedding-001', label: 'gemini-embedding-001 (up to 3072d)', provider: 'gemini' },
    { value: 'text-embedding-3-small', label: 'OpenAI text-embedding-3-small (1536d)', provider: 'openai' },
    { value: 'text-embedding-3-large', label: 'OpenAI text-embedding-3-large (3072d)', provider: 'openai' },
];

const PROVIDER_LABELS = { sentence_transformer: 'SentenceTransformer', gemini: 'Gemini', openai: 'OpenAI' };

export default function AdminRAGSettings() {
    const [settings, setSettings] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await adminApi.getRAGSettings();
            setSettings(data);
            setFormData({ ...data });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleModelChange = (model) => {
        const preset = ALL_MODELS.find(m => m.value === model);
        if (!preset) return;
        const supportsCustomDims = ['gemini', 'openai'].includes(preset.provider);
        setFormData(prev => ({
            ...prev,
            embedding_model: model,
            embedding_provider: preset.provider,
            embedding_dimensions: supportsCustomDims ? (prev.embedding_dimensions || 768) : null,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const updated = await adminApi.updateRAGSettings(formData);
            setSettings(updated);
            setFormData({ ...updated });
            setEditing(false);
            setSuccessMessage('Settings updated successfully');
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({ ...settings });
        setEditing(false);
        setError(null);
    };

    if (loading) return <Spinner size="lg" text="Loading settings..." center />;

    const modelChanged = formData?.embedding_model !== settings?.embedding_model;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link
                    to="/admin/rag/manage"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </Link>
                <h1 className="text-lg font-semibold text-gray-900">RAG Settings</h1>
                {!editing && (
                    <div className="ml-auto">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
                            Edit
                        </Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            {!editing ? (
                <div className="space-y-4">
                    <Card title="Embedding">
                        <InfoRow label="Vector Store" value={settings.vector_store} />
                        <InfoRow label="Provider" value={PROVIDER_LABELS[settings.embedding_provider] || settings.embedding_provider} />
                        <InfoRow label="Model" value={settings.embedding_model} mono />
                        <InfoRow label="Device" value={settings.embedding_device} />
                        {settings.embedding_dimensions && (
                            <InfoRow label="Dimensions" value={settings.embedding_dimensions} />
                        )}
                        <InfoRow label="Cache" value={settings.embedding_cache || 'memory'} />
                    </Card>
                    <Card title="Chunking">
                        <InfoRow label="Strategy" value={settings.chunk_strategy || 'recursive'} />
                        <InfoRow label="Chunk Size" value={settings.chunk_size} />
                        <InfoRow label="Chunk Overlap" value={settings.chunk_overlap} />
                    </Card>
                    <Card title="Reranker">
                        <InfoRow label="Provider" value={settings.reranker_provider || 'none'} />
                        {settings.reranker_provider && settings.reranker_provider !== 'none' && (
                            <>
                                <InfoRow label="Model" value={settings.reranker_model} mono />
                                <InfoRow label="Device" value={settings.reranker_device} />
                            </>
                        )}
                        <InfoRow label="Score Threshold" value={settings.score_threshold ?? 'None'} />
                    </Card>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Card title="Vector Store">
                        <Select
                            label="Provider"
                            value={formData.vector_store}
                            onChange={(e) => handleChange('vector_store', e.target.value)}
                            options={VECTOR_STORE_OPTIONS}
                        />
                    </Card>

                    <Card title="Embedding">
                        <Select
                            label="Model"
                            value={formData.embedding_model}
                            onChange={(e) => handleModelChange(e.target.value)}
                            options={ALL_MODELS}
                            hint={`Provider: ${PROVIDER_LABELS[formData.embedding_provider] || formData.embedding_provider}`}
                        />
                        {formData.embedding_provider === 'sentence_transformer' && (
                            <Select
                                label="Device"
                                value={formData.embedding_device}
                                onChange={(e) => handleChange('embedding_device', e.target.value)}
                                options={DEVICE_OPTIONS}
                            />
                        )}
                        {['gemini', 'openai'].includes(formData.embedding_provider) && (
                            <Input
                                label="Output Dimensions"
                                type="number"
                                value={formData.embedding_dimensions || 768}
                                onChange={(e) => handleChange('embedding_dimensions', parseInt(e.target.value) || 768)}
                                hint={formData.embedding_provider === 'gemini' ? '128-3072' : '256-3072 (v3 models support truncation)'}
                            />
                        )}
                        {modelChanged && (
                            <Alert variant="warning" className="mt-2">
                                Changing embedding model will require reindexing all collections using default settings.
                            </Alert>
                        )}
                    </Card>

                    <Card title="Chunking">
                        <Select
                            label="Strategy"
                            value={formData.chunk_strategy || 'recursive'}
                            onChange={(e) => handleChange('chunk_strategy', e.target.value)}
                            options={[
                                { value: 'recursive', label: 'Recursive (character-based)' },
                                { value: 'token', label: 'Token (tiktoken-based)' },
                            ]}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label="Chunk Size"
                                type="number"
                                value={formData.chunk_size}
                                onChange={(e) => handleChange('chunk_size', parseInt(e.target.value) || 512)}
                                hint="64-4096"
                            />
                            <Input
                                label="Chunk Overlap"
                                type="number"
                                value={formData.chunk_overlap}
                                onChange={(e) => handleChange('chunk_overlap', parseInt(e.target.value) || 50)}
                                hint="0-512"
                            />
                        </div>
                    </Card>

                    <Card title="Reranker">
                        <Select
                            label="Provider"
                            value={formData.reranker_provider || 'none'}
                            onChange={(e) => handleChange('reranker_provider', e.target.value)}
                            options={[
                                { value: 'none', label: 'None' },
                                { value: 'cross_encoder', label: 'Cross-Encoder (local model)' },
                                { value: 'gemini', label: 'Gemini (API-based)' },
                            ]}
                        />
                        {formData.reranker_provider === 'cross_encoder' && (
                            <>
                                <Select
                                    label="Model"
                                    value={formData.reranker_model || 'AITeamVN/Vietnamese_Reranker'}
                                    onChange={(e) => handleChange('reranker_model', e.target.value)}
                                    options={[
                                        { value: 'AITeamVN/Vietnamese_Reranker', label: 'Vietnamese Reranker' },
                                        { value: 'BAAI/bge-reranker-v2-m3', label: 'BGE Reranker v2 M3' },
                                    ]}
                                />
                                <Select
                                    label="Device"
                                    value={formData.reranker_device || 'cpu'}
                                    onChange={(e) => handleChange('reranker_device', e.target.value)}
                                    options={DEVICE_OPTIONS}
                                />
                            </>
                        )}
                        <Select
                            label="Embedding Cache"
                            value={formData.embedding_cache || 'memory'}
                            onChange={(e) => handleChange('embedding_cache', e.target.value)}
                            options={[
                                { value: 'memory', label: 'In-Memory' },
                                { value: 'redis', label: 'Redis' },
                            ]}
                        />
                        <Input
                            label="Score Threshold"
                            type="number"
                            value={formData.score_threshold || ''}
                            onChange={(e) => handleChange('score_threshold', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="None"
                            hint="0.0-1.0"
                        />
                    </Card>

                    <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                        <Button variant="ghost" type="button" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={saving}>
                            Save Settings
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
